import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip as MapTooltip, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { MapPin, Users, Layers } from "lucide-react";

function colorScale(count, max) {
  if (max === 0 || count === 0) return "#94A3B8"; // grey
  const t = count / max;
  // soft → vivid gradient
  if (t < 0.2) return "#0EA5E9";
  if (t < 0.4) return "#10B981";
  if (t < 0.6) return "#F59E0B";
  if (t < 0.8) return "#F43F5E";
  return "#E11D48";
}

export default function Heatmap() {
  const [data, setData] = useState(null);
  const [mandalFilter, setMandalFilter] = useState("all");

  useEffect(() => {
    const qs = mandalFilter === "all" ? "" : `?mandal=${encodeURIComponent(mandalFilter)}`;
    api.get(`/dashboard/heatmap${qs}`).then(({ data }) => setData(data));
  }, [mandalFilter]);

  if (!data) return <div className="p-10 text-gray-500">Loading map…</div>;

  const filtered = mandalFilter === "all"
    ? data.points
    : data.points.filter((p) => p.mandal === mandalFilter);
  const mandals = Array.from(new Set(data.points.map((p) => p.mandal))).filter(Boolean);

  const totalShown = filtered.reduce((sum, p) => sum + p.count, 0);

  return (
    <div className="p-6 lg:p-8" data-testid="heatmap-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E11D48] font-semibold">Geographic View</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-1">Village Heatmap</h1>
          <p className="text-gray-600 text-sm">
            {filtered.length} villages plotted ·{" "}
            <span className="font-heading font-bold text-[#1E3A8A]">{data.total_youth ?? totalShown}</span> youth registered
            {data.orphan_count > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-medium">
                ⚠ {data.orphan_count} orphan record(s) — village name not in constituency list
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full p-1">
          <button
            onClick={() => setMandalFilter("all")}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              mandalFilter === "all" ? "bg-[#1E3A8A] text-white" : "text-gray-600"
            }`}
            data-testid="mandal-filter-all"
          >
            All
          </button>
          {mandals.map((m) => (
            <button
              key={m}
              onClick={() => setMandalFilter(m)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                mandalFilter === m ? "bg-[#1E3A8A] text-white" : "text-gray-600"
              }`}
              data-testid={`mandal-filter-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="relative" style={{ height: "640px" }}>
          <MapContainer
            center={[data.center.lat, data.center.lng]}
            zoom={11}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filtered.map((p) => {
              const radius = 8 + Math.sqrt(p.count) * 4;
              const color = colorScale(p.count, data.max_count);
              return (
                <CircleMarker
                  key={p.village_id}
                  center={[p.lat, p.lng]}
                  radius={radius}
                  pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: 0.55,
                    weight: 2,
                  }}
                >
                  <MapTooltip direction="top" offset={[0, -radius]} permanent={p.count > 0}>
                    <div className="font-heading font-semibold">{p.name}</div>
                    {p.count > 0 && <div className="text-xs">{p.count} registered</div>}
                  </MapTooltip>
                  <Popup>
                    <div className="font-heading font-semibold text-base">{p.name}</div>
                    <div className="text-xs text-gray-500 mb-2">{p.mandal} Mandal</div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-3.5 h-3.5 text-[#1E3A8A]" />
                      <span><b>{p.count}</b> youth registered</span>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {/* Legend overlay */}
          <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-xl shadow-lg p-4 border border-gray-200 z-[1000]">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1">
              <Layers className="w-3 h-3" /> Density (Max: {data.max_count})
            </div>
            <div className="space-y-1.5">
              {[
                { c: "#94A3B8", label: "0 registered" },
                { c: "#0EA5E9", label: "Few" },
                { c: "#10B981", label: "Some" },
                { c: "#F59E0B", label: "Many" },
                { c: "#F43F5E", label: "High" },
                { c: "#E11D48", label: "Top" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: b.c }} />
                  <span>{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
        <Kpi color="#1E3A8A" icon={<MapPin />} label="Villages with registrations" value={filtered.filter((p) => p.count > 0).length} sub={`of ${filtered.length} mapped`} />
        <Kpi color="#F59E0B" icon={<Users />} label="Total youth registered" value={totalShown} sub={mandalFilter === "all" ? "across the constituency" : `in ${mandalFilter} mandal`} />
        <Kpi color="#E11D48" icon={<Layers />} label="Hottest village" value={(filtered.sort((a, b) => b.count - a.count)[0]?.name) || "—"} sub={`${filtered.sort((a, b) => b.count - a.count)[0]?.count || 0} registered`} />
      </div>
    </div>
  );
}

function Kpi({ color, icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white mb-3" style={{ backgroundColor: color }}>{icon}</div>
      <div className="font-heading font-bold text-2xl tracking-tight">{value}</div>
      <div className="text-sm text-gray-700 font-medium mt-0.5">{label}</div>
      <div className="text-xs text-gray-500">{sub}</div>
    </div>
  );
}
